import type { RichTextContent } from '@beamery/component-rich-text';
import type { ConditionsOutcomes } from './conditions';

export const TEXT_ELEMENT_MAX_LENGTH = 250;

export interface BaseElement {
  id: string;
  conditions?: ConditionsOutcomes;
}

interface BlockElement extends BaseElement {
  required: null;
  field: null;
}

export type ElementValue = string | string[] | null | undefined;

export enum ElementType {
  image = 'image',
  richtext = 'richtext',
  text = 'text',
  email = 'email',
  phone = 'phone',
  typeahead = 'typeahead',
  select = 'select',
  date = 'date',
  file = 'file',
  dataProtection = 'dataProtection',
  button = 'button',
  divider = 'divider',
  companyLogo = 'companyLogo',
  socialIcons = 'socialIcons',
  multiText = 'multiText',
}

export interface InputElement extends BaseElement {
  required: boolean;
  field: string;
  value?: ElementValue;
}

export interface ImageElement extends BlockElement {
  type: ElementType.image;
  meta: {
    alt: string;
    src?: string;
    filename?: string;
  };
}

export interface RichTextElement extends BlockElement {
  type: ElementType.richtext;
  meta: {
    content: RichTextContent;
  };
}

export interface TextElement extends InputElement {
  type: ElementType.text;
  meta: {
    label: string;
  };
}

export interface MultiTextElement extends InputElement {
  type: ElementType.multiText;
  meta: {
    label: string;
  };
}

export interface EmailElement extends InputElement {
  type: ElementType.email;
  meta: {
    label: string;
    messages?: {
      invalidFormatMessage?: string;
    };
  };
}

export interface PhoneElement extends InputElement {
  type: ElementType.phone;
  meta: {
    label: string;
  };
}

export interface TypeaheadElement extends InputElement {
  type: ElementType.typeahead;
  meta: {
    label: string;
    multi: boolean;
    source: 'location' | 'universities';
  };
}

export interface SelectOption {
  label: string;
  value: string;
}

export interface SelectElement extends InputElement {
  type: ElementType.select;
  meta: {
    label: string;
    multi: boolean;
    options: SelectOption[];
    sortOptionsAlphabetically?: boolean;
  };
}

export interface DateElement extends InputElement {
  type: ElementType.date;
  meta: {
    label: string;
  };
}

export interface FileElement extends InputElement {
  type: ElementType.file;
  meta: {
    label: string;
    sizeLimit: number;
    acceptedTypes: string[];
    purpose?: 'resume';
    messages?: {
      uploadGuidanceMessage?: string;
      dragAndDropText?: string;
      uploadButton?: string;
      sizeLimitLabel?: string;
      sizeLimitMessage?: string;
      fileTypesLabel?: string;
      fileTypesMessage?: string;
      loadingLabel?: string;
      removeFileLabel?: string;
    };
  };
}

export interface DataProtectionElement extends BlockElement {
  type: ElementType.dataProtection;
  meta: {
    statement?: {
      id: string | null;
    };
  };
  value?: boolean;
}

export interface ButtonElement extends BlockElement {
  type: ElementType.button;
  meta: {
    label: string;
    action: 'submit' | 'reset';
  };
}

export interface DividerElement extends BlockElement {
  type: ElementType.divider;
  meta: null;
}

export interface CompanyLogoElement extends BlockElement {
  type: ElementType.companyLogo;
  meta: null;
}

export interface SocialIconsElement extends BlockElement {
  type: ElementType.socialIcons;
  meta: null;
}

export type FormInputElement =
  | TextElement
  | MultiTextElement
  | EmailElement
  | PhoneElement
  | TypeaheadElement
  | SelectElement
  | DateElement
  | FileElement;

export type FormBlockElement =
  | ImageElement
  | RichTextElement
  | DataProtectionElement
  | ButtonElement
  | DividerElement
  | CompanyLogoElement
  | SocialIconsElement;

export type FormElement = FormInputElement | FormBlockElement;
export type PageType = 'form' | 'success';
export type SubmitAction = 'proceed' | 'alert' | 'login';

export interface Page {
  id: string;
  title: string;
  elements: FormElement[];
  pageType: PageType;
  onSubmit: SubmitAction;
}

interface CookieConsentApiKeys {
  events: string;
  pages: string;
  flows: string;
}

export interface CookieConsent {
  enabled: boolean;
  apiKeys: CookieConsentApiKeys;
  cdn: string;
  provider: string;
  settingsButtonCss: string | null;
  test: boolean;
}

export interface PublicFormCompliance {
  title: string;
  consentRequestIntroductionStatement: string;
  consentQuestion: string;
  privacyPolicyUrl: string;
  privacyPolicyText: string;
  version: 1 | 2;
}

export interface SavedFormCompliance extends PublicFormCompliance {
  id: string | null;
  isDefault: boolean;
  name: string | null;
}

export interface FormSettingsBase {
  slug: string;
  externalName: string;
  useCustomBranding: boolean;
  customBrandName?: string;
  customBrandLogoUrl?: string;
  authenticated?: boolean;
}

export interface SavedFormSettings extends FormSettingsBase {
  internalName: string;
  slug: string;
}

export interface FormUserProfile {
  id: string;
}

export interface SocialMedia {
  twitter?: string;
  youtube?: string;
  facebook?: string;
  linkedin?: string;
  instagram?: string;
  glassdoor?: string;
  wechat?: string;
}

export interface BaseFormCompanySettings {
  accentColor: string | null;
  companyLogoUrl: string;
  companyName: string;
  companyId: string;
  companySlug: string;
  cookieSettings: CookieConsent;
  gaKey: string | null;
  vanityUrls: string[] | null;
  customFontUrl: string | null;
  customFontFamilyName: string | null;
  socialMedia: SocialMedia | null;
}

export interface PublicFormCompanySettings extends BaseFormCompanySettings {
  compliances: PublicFormCompliance[];
  gtmKey: string | null;
  recaptchaEnabled: boolean;
}

export interface SavedFormCompanySettings extends BaseFormCompanySettings {
  compliances: SavedFormCompliance[];
}

export interface FlexibleOptions {
  singleSelectPlaceholder?: string | null;
  singleSelectFilterText?: string | null;
  multiSelectPlaceholder?: string | null;
  multiSelectFilterText?: string | null;
  multiSelectSelectedText?: string | null;
  requiredFieldErrorMessage?: string | null;
  typeaheadNoResultsMessage?: string | null;
  characterLimitMessage?: string | null;
  submitToastMessage?: string | null;
  backButtonText?: string | null;
  nextButtonText?: string | null;
  restartButtonText?: string | null;
  submitButtonText?: string | null;
}

export interface Event {
  id: string;
}

export interface Pool {
  id: string;
}

export interface FormDefinitionBase {
  id: string;
  pages: Page[];
  modified: string;
  settings: FormSettingsBase;
  event?: Event;
  pool?: Pool | null;
  flexibleOptions: FlexibleOptions | null;
}

export interface PublicFormDefinition extends FormDefinitionBase {
  authenticated: boolean | null;
  companySettings: PublicFormCompanySettings;
}

export interface SavedFormDefinition extends FormDefinitionBase {
  status: 'published' | 'unpublished' | 'draft';
  settings: SavedFormSettings;
  companySettings: SavedFormCompanySettings;
  created: string;
  profile: FormUserProfile;
}
